'use client'

import { cn } from '@/lib/utils'
import { EASE, Eyebrow, HAIRLINE, useReveal } from '@/components/page-kit'

import {
  DeliversHeading,
  useSolution,
  type Solution,
  type SolutionSlug,
} from './solution-kit'

/* Cloud's device: THE STACK.

   Infrastructure is strata — the authored copy calls it "the invisible engine"
   underneath everything, with UDC's modules running on top. So the six things
   the architecture delivers are drawn as six full-width layers stacked on one
   another, each labelled in a left gutter, rather than as a 3-column card grid.

   Distinct from the product devices: UDC's bus is parallel modules on one line,
   HRMS's track runs in one direction, Payroll/Accounts/LMS are ruled documents.
   A stack reads vertically, and the depth index in the gutter is what sells it. */

export function CloudStack({ slug }: { slug: SolutionSlug }) {
  const solution = useSolution(slug)
  const stack = useReveal<HTMLDivElement>(0.08)

  return (
    <section id="delivers" className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <Eyebrow>The stack</Eyebrow>
        <DeliversHeading solution={solution} />

        <div
          ref={stack.ref}
          className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}
        >
          {solution.delivers.map((d, i) => {
            const Glyph = d.icon
            return (
              <div
                key={d.title}
                className={cn(
                  'group relative grid grid-cols-1 gap-x-8 gap-y-2 border-b py-6 sm:py-7',
                  'lg:grid-cols-[7rem_minmax(0,280px)_1fr] lg:items-baseline',
                  HAIRLINE
                )}
                style={{
                  opacity: stack.shown ? 1 : 0,
                  transform: stack.shown ? 'none' : 'translateY(10px)',
                  transition: `opacity 520ms ${EASE} ${i * 80}ms, transform 520ms ${EASE} ${i * 80}ms`,
                }}
              >
                {/* Depth index — the layer's position in the stack, counted
                    from the top down. */}
                <div className="flex items-baseline gap-3 lg:block">
                  <span className="text-[11px] tabular-nums text-muted-foreground/60">
                    L{String(solution.delivers.length - i).padStart(2, '0')}
                  </span>
                  <span
                    aria-hidden
                    className="hidden h-px w-10 bg-border align-middle lg:mt-2 lg:inline-block"
                  />
                </div>

                <h3 className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl">
                  {d.title}
                </h3>

                <div className="flex items-start gap-4">
                  <p className="max-w-2xl flex-1 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {d.text}
                  </p>
                  <Glyph
                    aria-hidden
                    className="mt-0.5 hidden size-5 shrink-0 text-foreground/30 lg:block"
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* The four delivery steps, set as the stack being built from the ground up. */
export function CloudSteps({ slug }: { slug: SolutionSlug }) {
  const solution = useSolution(slug)
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <Eyebrow>How it works</Eyebrow>

        <div ref={list.ref} className={cn('mt-8 border-t sm:mt-10', HAIRLINE)}>
          {solution.howItWorks.map((s, i) => (
            <div
              key={s.step}
              className={cn(
                'grid grid-cols-1 gap-x-8 gap-y-2 border-b py-6 sm:py-7',
                'lg:grid-cols-[4rem_minmax(0,280px)_1fr] lg:items-baseline',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(10px)',
                transition: `opacity 520ms ${EASE} ${i * 90}ms, transform 520ms ${EASE} ${i * 90}ms`,
              }}
            >
              <span className="font-heading text-lg font-normal tabular-nums text-emerald-600 sm:text-xl dark:text-primary-active">
                {s.step}
              </span>

              <h3 className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl">
                {s.title}
              </h3>

              <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                {s.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
